import {
  LogisticsTransportApiFactory,
  LogisticsTransportApiTransportationCompaniesListRequest,
  type LogisticsTransportApiTransportationPartnersCreateRequest,
  LogisticsTransportApiTransportationPartnersListRequest,
  type PatchedCompanyTransportPartnerRequest,
} from '@/clients/unifiedApi';
import { ServerListViewChangeData } from '@/components/ServerListView/ServerListViewChangeData';
import { URLS } from '@/constants';
import { PaginatedResponse } from '@/models/api';
import { PaymentMethod, PaymentTerm } from '@/models/company';
import {
  BuyerCompany,
  DeliveryOrder,
  InboundOrder,
  InboundOrderAsns,
  InboundOrderItem,
  LogisticsFirstOrder,
  LogisticsFirstShipment,
  LogisticsOrder,
  LogisticsOrderForAdmin,
  LogisticsShipment,
  PurchaseOrder,
  RouteSchedule,
  TransportOrderDestinationResponse,
  TransportSchedule,
} from '@/models/logistics';
import { useUserStore } from '@/piniaStores/user/user.store';
import { getUnifiedApi } from '@/services/api/unifiedApi';
import session, { apiSlugSession, createSession } from '@/services/session';
import { buildPath } from '@/utils';
import JSONTransform from '@/utils/json-transform';
import getPrevAndNextLinks from '@/utils/paginationHelpers';
import { objectToQueryString } from '@/utils/urlHelpers';

/**
 * Get Logistics Orders
 * @param {string} params Any parameters for the endpoint.
 * @return {Promise<PaginatedResponse>}
 */
export async function getOrders<T = LogisticsOrder>(params?: string) {
  const url = `${URLS.LOGISTICS.TRANSPORT_ORDERS}/?${URLS.CUSTOM_ERROR}&${params}`;
  const response = await apiSlugSession.get<PaginatedResponse<T>>(url);

  return response.data;
}

/**
 * Get Products from API
 * @type {object} params Any parameters for the endpoint.
 * @return {DeliveryOrder[]}
 */
export const getDeliveriesOrders = async (params?: object): Promise<DeliveryOrder[]> => {
  const response = await apiSlugSession.get(
    `${URLS.LOGISTICS.BASE}/${URLS.LOGISTICS.DELIVERIES_ORDERS}/?${URLS.CUSTOM_ERROR}&limit=10000`,
    {
      params: {
        ...params,
      },
    },
  );

  return response.data.results;
};

/**
 * Get Products from API
 * @type params - Any parameters for the endpoint.
 */
export const getShipments = async (params: ServerListViewChangeData['serialized']) => {
  const url = `${URLS.LOGISTICS.SHIPMENTS}/?${URLS.CUSTOM_ERROR}&${params}`;
  const response = await apiSlugSession.get<PaginatedResponse<LogisticsShipment | LogisticsFirstShipment>>(url);

  return response.data;
};

export const getShipmentsCsv = async (params: ServerListViewChangeData['serialized']) => {
  const url = `${URLS.LOGISTICS.SHIPMENTS}/?${URLS.CUSTOM_ERROR}&${params}`;
  const response = await apiSlugSession.get<string>(url, {
    headers: {
      accept: 'text/csv',
    },
  });

  return response.data;
};

/**
 * Create a transport order
 * @type {object} body to send with the post request.
 */
export const assignTransportOrder = async (body) => {
  const url = `${URLS.LOGISTICS.TRANSPORT_ORDERS}/?${URLS.CUSTOM_ERROR}`;
  const response = await apiSlugSession.post<LogisticsOrder>(url, body);

  return response.data;
};

/**
 * Update a logistics order
 * @type {object} body to send with the post request.
 */
export const updateOrder = async (payload: {
  number: string;
  requestedDeliveryDate: string;
  requestedPickupDate: string;
  eligiblePaymentMethods: string[];
}) => {
  const { number, ...payloadToSend } = payload;
  const response = await apiSlugSession.patch<LogisticsOrder>(
    `${URLS.LOGISTICS.BASE}/${URLS.LOGISTICS.ORDERS}/${number}/?${URLS.CUSTOM_ERROR}`,
    {
      ...payloadToSend,
    },
  );

  return response.data;
};

export interface UpdateTransportOrderPayload {
  number: string;
  l3Order: {
    paymentMethod: PaymentMethod['id'];
    paymentTerm: PaymentTerm['id'];
    requestedDeliveryDate: string;
    requestedPickupDate: string;
  };
  boxCount: number;
  paymentAmount: number;
  customerNotes: string;
}

/**
 * Update a logistics transport order
 * @param payload the payload to send with the request
 */
export const updateTransportOrder = async (payload: UpdateTransportOrderPayload) => {
  const { number, l3Order, boxCount, ...remainingPayload } = payload;

  const serializedParams = new JSONTransform(remainingPayload).toSnakeCaseKeys();

  const requestedParams = {
    ...serializedParams,
    box_count: boxCount || null,
    l3_order: new JSONTransform(l3Order).toSnakeCaseKeys(),
  };

  const userStore = useUserStore();

  const path = buildPath(URLS.LOGISTICS.UPDATE_TRANSPORT_ORDER, { orderId: number });
  const url = `${path}?${URLS.CUSTOM_ERROR}`;
  const session = createSession(URLS.INTERNAL + userStore.userActiveCompany?.slug, { shouldTransform: false });
  const { data } = await session.patch<LogisticsFirstOrder>(url, requestedParams);

  return data;
};

/**
 * Delete a transport order
 * @param payload.isLogisticsOnly - If the order is a Logistics-only transport order
 * @param payload.number The transport order number
 */
export const deleteTransportOrder = async (payload: { isLogisticsOnly: boolean; number: string }) => {
  const path = payload.isLogisticsOnly
    ? buildPath(URLS.LOGISTICS.TRANSPORT_ONLY_ORDER, { orderId: payload.number })
    : buildPath(URLS.LOGISTICS.TRANSPORT_ORDER, { orderId: payload.number });
  const url = `${path}?${URLS.CUSTOM_ERROR}`;
  const response = await apiSlugSession.delete(url);

  return response.data;
};

/**
 * Get transport order details
 */
export const getTransportOrderRouteDetails = async (payload: { orderNumber: string }): Promise<PaginatedResponse> => {
  const url = `${URLS.LOGISTICS.BASE}/${URLS.LOGISTICS.ORDERS}/${payload.orderNumber}/${URLS.LOGISTICS.L3_ORDER_ROUTE_DETAILS}/?${URLS.CUSTOM_ERROR}`;

  const response = await apiSlugSession.get(url);

  return response.data;
};

/**
 * Get transport order directions
 */
export const getTransportOrderRouteDirections = async (payload: {
  orderNumber: string;
}): Promise<PaginatedResponse> => {
  const url = `${URLS.LOGISTICS.BASE}/${URLS.LOGISTICS.ORDERS}/${payload.orderNumber}/${URLS.LOGISTICS.L3_ORDER_ROUTE_DIRECTIONS}/?${URLS.CUSTOM_ERROR}`;

  const response = await apiSlugSession.get(url);

  return response.data;
};

/**
 * Regenerate Metrc transfer template for a Transport Order
 */
export const createMetrcTransferTemplate = async (payload: { orderNumber: string }): Promise<PaginatedResponse> => {
  const url = `${URLS.LOGISTICS.BASE}/${URLS.LOGISTICS.ORDERS}/${payload.orderNumber}/${URLS.LOGISTICS.TRANSFER}/?${URLS.CUSTOM_ERROR}`;

  const response = await apiSlugSession.post(url);

  return response.data;
};

/**
 * Get pickup stops for the current selected companies.
 * @param {object} [payload] Object with payload data.
 * @param {string} [payload.pickup_date] The selected pickup date.
 * @param {number[]} [payload.companies] Array of companies ids to be pushed.
 * @return {Promise<Array>}
 */
export const fetchPickupStops = async (payload: { pickup_date: string; companies: Array<number> }) => {
  const response = await session.get(
    `${URLS.LOGISTICS.BRINGG.GET_PICKUP_STOPS}?pickup_date=${payload.pickup_date}&companies=${payload.companies.join(
      '&companies=',
    )}`,
  );

  return response.data;
};

/**
 * Get transport delivery schedule for given zipcodes.
 * @param {object} [payload] Object with payload data.
 * @param {Array[]} [payload.zipcodes] Array of zipcodes to be pushed.
 * @return {Promise<Array>}
 */
export const getTransportDeliverySchedulesValidDates = async (payload: { zipcodes: string[] }) => {
  const userStore = useUserStore();

  const joinedZipcodes = payload.zipcodes.join();
  const response = await session.get<{ results: RouteSchedule }>(
    `${userStore.userActiveCompany?.slug}/${URLS.LOGISTICS.BASE}/${URLS.LOGISTICS.TMS.TRANSPORT_DELIVERY_SCHEDULES_ZIPCODES}/`,
    { params: { zipcodes: joinedZipcodes } },
  );

  return response.data;
};

/**
 * Get transport pickup schedule for given zipcodes.
 * @param [payload] Object with payload data.
 * @param [payload.zipcodes] Array of zipcodes to be pushed.
 */
export const getTransportPickupSchedulesValidDates = async (payload: { zipcodes: Array<string> }) => {
  if (!payload?.zipcodes?.length) {
    return [];
  }

  const userStore = useUserStore();

  const response = await session.get(
    `${userStore.userActiveCompany?.slug}/${URLS.LOGISTICS.BASE}/${URLS.LOGISTICS.TMS.TRANSPORT_PICKUP_SCHEDULES_ZIPCODES}/`,
    { params: { zipcodes: payload.zipcodes.join() } },
  );

  return response.data;
};

/**
 * Push pick stops to bring.
 * @param [payload] Object with payload data.
 * @param [payload.pickupDate] The selected pickup date.
 * @param [payload.state] The selected state of the current orders list.
 * @param [payload.stops] Array with stops numbers to be pushed.
 */
export const pushPickupStops = async (payload: { state: string; pickupDate: string; stops: Array<number> }) => {
  const response = await session.post(`${URLS.LOGISTICS.BRINGG.PUSH_PICKUP_STOPS}`, payload);

  return response.data;
};

/**
 * Get a list of companies that have logistics enabled
 * @param {String} params to filter the endopoint
 * @return {Promise<PaginatedResponse>}
 */
export const getLogisticsCompanies = async (params): Promise<PaginatedResponse> => {
  const response = await session.get(`${URLS.LOGISTICS.COMPANIES}/?limit=999&${params}`);

  return response.data.results;
};

/**
 * Get a list of states that have logistics enabled
 */
export const getLogisticsStates = async () => {
  const response = await session.get<PaginatedResponse>(`leaflink/${URLS.STATES.LIST}?logistics_enabled=true`);

  return response.data;
};

/**
 * Create ETOS value on delivery preferences
 * @type {object} body to send with the patch request.
 */
export const createEtosValue = async (payload) => {
  const { id: _id, ...payloadToSend } = payload;

  const response = await session.post(`${URLS.LOGISTICS.DELIVERY_PREFERENCES}/?${URLS.CUSTOM_ERROR}`, {
    ...payloadToSend,
  });

  return response.data;
};

/**
 * Update ETOS value on delivery preferences
 * @type {object} body to send with the patch request.
 */
export const updateEtosValue = async (payload) => {
  const { id, ...payloadToSend } = payload;

  const response = await session.patch(`${URLS.LOGISTICS.DELIVERY_PREFERENCES}/${id}/?${URLS.CUSTOM_ERROR}`, {
    ...payloadToSend,
  });

  return response.data;
};

/**
 * Get a list of logistics orders for administrative usage.
 * @param {String} params to filter the endopoint
 */
export const getOrdersForAdmin = async (params) => {
  const response = await session.get<PaginatedResponse<LogisticsOrderForAdmin>>(
    `${URLS.LOGISTICS.LOGISTICS_ORDERS}?${params}`,
  );

  return response.data;
};

/**
 * Push Orders to Bringg
 * @type {object} body to send with the post request.
 */
export const pushOrdersToBringg = async (payload) => {
  const response = await session.post(`${URLS.LOGISTICS.BRINGG.PUSH_ORDERS}`, {
    logisticsOrders: payload,
  });

  return response.data;
};

/**
 * Push delivery stops
 * @param {stopsId} stops ids to send
 * @return { [key: string]: string } { "stopId": "STATE" } Being state `success` or 'failed'
 */
export const pushDeliveryStops = async (stopsId: number[]): Promise<{ [key: string]: string }> => {
  const response = await session.post(URLS.LOGISTICS.BRINGG.PUSH_DELIVERY_STOPS, {
    logisticsOrders: stopsId,
  });

  return response.data;
};

/**
 * Get buyers and can be filter by verified or unverified.
 */
export const getBuyers = async (params = '') => {
  const response = await session.get<PaginatedResponse>(`${URLS.LOGISTICS.BUYERS}?${params}`);

  return response.data;
};

/**
 * Update company store hours
 */
export async function updateCompanyHours({
  id,
  storeHours,
}: {
  id: number;
  storeHours: Array<{
    dayOfTheWeek: number;
    openTime: string;
    closeTime: string;
  }>;
}) {
  const response = await session.patch(`${URLS.ADMIN.COMPANIES}/${id}/?${URLS.CUSTOM_ERROR}`, {
    storeHours,
  });

  return response.data;
}

/**
 * Get unlinked customers by selected state.
 */
export const fetchUnlinkedCustomers = async (params = '') => {
  const response = await session.get<PaginatedResponse>(`${URLS.LOGISTICS.UNLINKED_CUSTOMERS}/?${params}`);

  return response.data;
};

/**
 * Get Buyer Companies from API
 */
export const getBuyerCompanies = async (params: {
  displayType: string;
  licenseNumber: string;
  state: string;
  name: string;
  isBuyer: boolean;
}) => {
  const serializedParams = objectToQueryString(new JSONTransform(params).toSnakeCaseKeys());

  const response = await session.get<PaginatedResponse<BuyerCompany>>(`${URLS.ADMIN.COMPANIES}?${serializedParams}`);

  return response.data.results;
};

/**
 * Create Buyer or validate fields API
 * @param payload data to send to api
 * @param payload.validateOnly flag to set if should only validate
 */
export const createBuyer = async (payload: {
  validateOnly?: boolean;
  name: string;
  buyer: boolean;
  licenses: Array<unknown>;
  deliveryPreferences: string;
  deliveryAddress: object;
  contactPersons: Array<unknown>;
  companyHours: Array<unknown>;
}) => {
  const { validateOnly, ...payloadToSend } = payload;
  const validateQueryString = validateOnly ? `?validate_only=${validateOnly}` : '';

  const response = await session.post(`${URLS.ADMIN.COMPANIES}/${validateQueryString}`, payloadToSend);

  return response.data;
};

/**
 * Get Buyer Companies by ID from API
 * @return {BuyerCompany[]}
 */
export const getBuyerCompaniesById = async (companyId: number): Promise<BuyerCompany[]> => {
  const response = await session.get(`${URLS.ADMIN.COMPANIES}/${companyId}/`);

  return response.data;
};

/**
 * Update Companies by ID from API
 * @param params payload to send to API
 * @param params.id Company id to be updated
 * @return {BuyerCompany[]}
 */
export const updateCompany = (params): Promise<BuyerCompany[]> => {
  const { id, ...payload } = params;

  return session.patch(`${URLS.ADMIN.COMPANIES}/${id}/`, payload);
};

/**
 * Get license type by states API
 * @param params.state selected state send to API
 * @param params.isBuyerLicense check if want to filter by BuyerLicense
 */
export const getLicenseTypes = async (params: { state: string; isBuyerLicense: boolean }) => {
  const { state, isBuyerLicense = false } = params;
  const isBuyerLicenseParam = isBuyerLicense ? `&is_buyer_license=${isBuyerLicense}` : '';

  const response = await session.get(`${URLS.ADMIN.LICENSE_TYPES}/?state=${state}${isBuyerLicenseParam}`);

  return response.data;
};

/**
 * Links a Buyer Company to a Customer
 */
export const linkBuyerCompany = (customerId: number, partner: number): Promise<BuyerCompany[]> => {
  const serializedParams = objectToQueryString({ partner });

  return session.patch(`${URLS.LOGISTICS.UNLINKED_CUSTOMERS}/${customerId}/`, serializedParams);
};

/**
 *  Get company staff by id
 * @param [params.companyId] id to fetch staffs of the company
 * @return {Promise<PaginatedResponse>}
 */
export const fetchCompanyStaffs = async (params: { companyId: number }): Promise<PaginatedResponse> => {
  const serializedParams = objectToQueryString({
    company: params.companyId,
    is_active: true,
    limit: 1000,
  });
  const response = await session.get(`${URLS.ADMIN.COMPANY_STAFFS}/?${serializedParams}`);

  return response.data;
};

/**
 * Get transport schedules for company group retailer locations for internal operations admins
 * @param payload.locationIds - list of retailer location ids
 */
export async function getTransportSchedules(payload: { locationIds: number[] }) {
  const { locationIds } = payload;

  if (!locationIds?.length) {
    return { results: [] };
  }

  const idSerialized = `company_ids=${locationIds.join(',')}`;

  const url = `${URLS.LOGISTICS.BASE}/${URLS.LOGISTICS.TMS.TRANSPORT_SCHEDULES}/?${URLS.CUSTOM_ERROR}&${idSerialized}`;

  const response = await apiSlugSession.get<{ results: TransportSchedule[] }>(url);

  return response.data.results;
}

/**
 * Save a transport schedule for company group retalier location
 * @param payload.id - location's id
 * @param payload.scheduledWeekDays - the selected week days for transport
 */
export async function saveDeliverySchedule(payload: { id: number; scheduledWeekDays: string[] }) {
  const { id, ...payloadToSend } = payload;

  const response = await apiSlugSession.post<TransportSchedule>(
    `${URLS.LOGISTICS.BASE}/${URLS.LOGISTICS.TMS.TRANSPORT_SCHEDULES}/?${URLS.CUSTOM_ERROR}&company_id=${id}`,
    payloadToSend,
  );

  return response.data;
}

/**
 * Get transport settings timezones
 * @return a list of Timezones
 */
export async function getTransportSettingsTimezones() {
  const response = await session.get(`${URLS.LOGISTICS.TMS.TRANSPORT_SETTINGS.TIMEZONES}/?${URLS.CUSTOM_ERROR}`);

  return response.data.timezones;
}

/* Get transport settings state markets
 * @return a list of State Markets
 */
export async function getTransportSettingsStateMarkets() {
  const response = await session.get(`${URLS.LOGISTICS.TMS.TRANSPORT_SETTINGS.STATE_MARKETS}/?${URLS.CUSTOM_ERROR}`);

  return response.data.settings;
}

/**
 * Get transport settings active states
 * @return a list of Active States
 */
export async function getTransportSettingsActiveStates() {
  const response = await session.get(`${URLS.LOGISTICS.TMS.TRANSPORT_SETTINGS.STATES}/active/?${URLS.CUSTOM_ERROR}`);

  return response.data.states;
}

/**
 * Update state market store hour
 */
export async function updateTransportSettingsStateMarket(stateMarket) {
  return await session.post(
    `${URLS.LOGISTICS.TMS.TRANSPORT_SETTINGS.STATE_MARKETS}/update/?${URLS.CUSTOM_ERROR}`,
    stateMarket,
  );
}

/**
 * Get eligible dates for orders assignment per buyer
 * @return {Promise<PaginatedResponse>}
 */
export const getTransportDates = async (params: { buyerIds: string[] }) => {
  const response = await session.get(`${URLS.LOGISTICS.TRANSPORT_DATES}/?buyer_ids=${params.buyerIds.join(',')}`);

  return response.data;
};

/**
 * Get All companies in state market
 */
export const getTransportOrderDestinations = async () => {
  const response = await apiSlugSession.get<PaginatedResponse<TransportOrderDestinationResponse>>(
    `${URLS.LOGISTICS.BASE}/${URLS.LOGISTICS.TRANSPORT_ORDER_DESTINATIONS}/?${URLS.CUSTOM_ERROR}&limit=9999`,
  );

  return response.data.results;
};

/**
 * Get all companies in state market in CSV format
 */
export const getTransportOrderDestinationsCsv = async () => {
  const response = await apiSlugSession.get(
    `${URLS.LOGISTICS.BASE}/${URLS.LOGISTICS.TRANSPORT_ORDER_DESTINATIONS}/?${URLS.CUSTOM_ERROR}&limit=9999`,
    {
      headers: {
        accept: 'text/csv',
      },
    },
  );

  return response.data;
};

/**
 * Create a transport order for Logistics First initiative
 * @type {object} params Any parameters for the endpoint.
 */
export const createTransportOrder = async (params) => {
  const userStore = useUserStore();

  const session = createSession(URLS.INTERNAL + userStore.userActiveCompany?.slug, { shouldTransform: false });
  const url = `${URLS.LOGISTICS.LOGISTICS_TRANSPORT_ORDERS}/?${URLS.CUSTOM_ERROR}`;

  return await session.post(url, params);
};

/**
 * Post form data to logistics upload endpoint
 * @param {FormData} [formData] form data with a `csv_file` field
 */
export const uploadTransportOrders = (formData: FormData) => {
  const userStore = useUserStore();

  const session = createSession(URLS.INTERNAL + userStore.userActiveCompany?.slug, { shouldTransform: false });

  return session.post(URLS.LOGISTICS.UPLOAD, formData);
};

/**
 * Get Inbound Order Mappings Transfers from API.
 * @return {Promise<PaginatedResponse>}
 */
export const getInboundOrderMappingsTransfers = async (params = ''): Promise<PaginatedResponse<InboundOrder>> => {
  const url = `${URLS.LOGISTICS.INBOUND_ORDER_MAPPPINGS.TRANSFERS}/?${params}`;
  const response = await session.get(url);
  const { prev, next } = getPrevAndNextLinks(url, response.data);

  return {
    ...response.data,
    prev,
    next,
  };
};

/**
 * Get Inbound Order Mappings Asns from API.
 * @return {Promise<PaginatedResponse>}
 */
export const getInboundOrderMappingsAsns = async (params = ''): Promise<PaginatedResponse<InboundOrderAsns>> => {
  const response = await session.get(`${URLS.LOGISTICS.INBOUND_ORDER_MAPPPINGS.ASNS}/?${params}`);

  return response.data.results;
};

/**
 * Get Inbound Order Mappings Asns from API.
 * @return {Promise<PaginatedResponse>}
 */
export const linkTransferToAsns = async (params): Promise<void> => {
  await session.put(URLS.LOGISTICS.INBOUND_ORDER_MAPPPINGS.LINK_TRANSFER_ASN, params);
};

/**
 * Get Inbound Order Mappings Items from API. TODO: Use endpoint when BE part is ready
 */
// eslint-disable-next-line require-await
export async function getInboundOrderMappingsItems(): Promise<PaginatedResponse<InboundOrderItem>> {
  const mockedResponse = {
    count: 0,
    next: null,
    prev: null,
    results: [],
  };

  return new Promise((resolve) => {
    setTimeout(() => {
      resolve(mockedResponse);
    }, 200);
  });
}

export interface GetUploadedPurchaseOrdersArgs {
  params: {
    page?: number;
    pageSize?: number;
    search?: string;
    ordering?: string;
  };
}

export async function getUploadedPurchaseOrders({ params }: GetUploadedPurchaseOrdersArgs) {
  const url = `${URLS.LOGISTICS.UPLOADED_PURCHASE_ORDERS}`;
  const paramsFormatted = new JSONTransform({ errorFormat: 'jsonapi', ...params }).toSnakeCaseKeys();
  const response = await apiSlugSession.get<PaginatedResponse<PurchaseOrder>>(url, { params: paramsFormatted });

  return response.data;
}

export const uploadOfflineOrders = async (formData: FormData) => {
  const userStore = useUserStore();

  const session = createSession(URLS.INTERNAL + userStore.userActiveCompany?.slug, { shouldTransform: false });
  const response = await session.post(URLS.LOGISTICS.UPLOAD_OFFLINE_ORDERS, formData);

  return response.data;
};

export async function updateUploadedPurchaseOrder({ id }: PurchaseOrder, update: Partial<PurchaseOrder>) {
  const response = await apiSlugSession.patch(`${URLS.LOGISTICS.UPLOADED_PURCHASE_ORDERS}/${id}/`, update);
  return response.data;
}

export async function deleteUploadedPurchaseOrder({ id }: PurchaseOrder) {
  const response = await apiSlugSession.delete(`${URLS.LOGISTICS.UPLOADED_PURCHASE_ORDERS}/${id}/`);
  return response.data;
}

/**
 * Get transport companies
 *
 * @param {LogisticsTransportApiTransportationCompaniesListRequest} params - The parameters for the request.
 * @return {Promise<PaginatedResponse>} - A promise that resolves to a paginated response containing transport companies.
 */
export async function getTransportCompanies(params: LogisticsTransportApiTransportationCompaniesListRequest) {
  const api = await getUnifiedApi(LogisticsTransportApiFactory);
  const response = await api.transportationCompaniesList(params);

  return response.data;
}

/**
 * Get company transport partner
 *
 * @param {LogisticsTransportApiTransportationPartnersListRequest} params - The parameters for the request.
 * @return {Promise<PaginatedResponse>} - A promise that resolves to a paginated response containing transport partners.
 */
export async function getCompanyTransportPartner(params: LogisticsTransportApiTransportationPartnersListRequest) {
  const api = await getUnifiedApi(LogisticsTransportApiFactory);
  const response = await api.transportationPartnersList(params);

  return response.data;
}

export interface UpdateCompanyTransportPartnerArgs {
  id: number;
  partner: PatchedCompanyTransportPartnerRequest;
}

/**
 * Add a new transport partner
 *
 * @param {LogisticsTransportApiTransportationPartnersCreateRequest} params - The parameters for creating a new transport partner.
 * @return {Promise<LogisticsTransportApiTransportationPartnersCreateRequest>} - A promise that resolves to the created transport partner data.
 */
export async function addCompanyTransportPartner(params: LogisticsTransportApiTransportationPartnersCreateRequest) {
  const api = await getUnifiedApi(LogisticsTransportApiFactory);
  const response = await api.transportationPartnersCreate(params);

  return response.data;
}

/**
 * Update company transport partner
 *
 * @param {UpdateCompanyTransportPartnerArgs} args - The arguments for updating the company transport partner.
 * @param {number} args.id - The ID of the company transport partner to update.
 * @param {PatchedCompanyTransportPartnerRequest} args.partner - The partner data to update.
 * @return {Promise<PatchedCompanyTransportPartnerRequest>} - A promise that resolves to the updated partner data.
 */
export async function updateCompanyTransportPartner({ id, partner }: UpdateCompanyTransportPartnerArgs) {
  const api = await getUnifiedApi(LogisticsTransportApiFactory);
  const response = await api.transportationPartnersPartialUpdate({
    patchedCompanyTransportPartnerRequest: partner,
    id,
  });

  return response.data;
}
